import type { CallOffsListResponse, CallOffsQueryParams } from './types';

import { ENDPOINT } from '~/constants/endpoints';

import { type KyRequestOptions } from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import { withoutObjectKeysWhereValueIs } from '@vestigas/utils';
import { log } from '~/utils';

import {
  zodCallOffsListResponseSchema,
  zodCallOffsQueryParamsSchema,
} from './validations';

/**
 * Fetches all call-offs from the API with the given query parameters.
 *
 * @param queryParams - The query parameters for fetching call-offs.
 * @param options - Additional request options.
 * @returns The paginated call-off data.
 * @throws Error if the API request fails.
 * @see https://app.dev.vestigas.com/redoc#tag/Call-Offs/operation/list_calloffs_calloffs_get
 */
export const fetchCallOffs = async (
  queryParams: Partial<CallOffsQueryParams>,
  options: KyRequestOptions,
) => {
  const response = await vestigasApi
    .get(ENDPOINT.CALL_OFF.GET_ALL(), {
      searchParams: queryParams,
      validate: {
        searchParams: zodCallOffsQueryParamsSchema
          .transform(({ limit, offset, page, ...rest }) => {
            // Translate page value to offset and remove 'page' key
            return {
              ...rest,
              limit,
              offset: page ? page * limit : offset,
            };
          })
          .transform((data) =>
            withoutObjectKeysWhereValueIs(data, [undefined, '']),
          ),
      },
      ...options,
    })
    .json<CallOffsListResponse | undefined>();

  // Validate the response with Zod
  const validatedResponse = zodCallOffsListResponseSchema.safeParse(response);
  if (validatedResponse?.error?.issues?.length) {
    log.error({
      message: 'CallOffs API response has validation errors',
      error: {
        errors: validatedResponse?.error?.issues,
        errorMessage: validatedResponse?.error?.message,
        response,
      },
    });
  }

  return response;
};
